import { MarkupType } from "../models/File";
import DateUtils from './DateUtils';
export class MarkupCommonUtils {
    /**
     * Width of the file-pages viewer page. Markup x/y/width/height are stored in this space
     * and PDF export must use the same width for its markup SVG viewBox.
     */
    static MARKUP_PAGE_WIDTH = 880;
    static RENDER_ORDER = {
        [MarkupType.DRAWING]: 0,
        [MarkupType.IMAGE]: 1,
        [MarkupType.SUBMITTAL]: 2,
        [MarkupType.RFI]: 3,
    };
    // ─── Style defaults ───────────────────────────────────────────────────────
    static DEFAULT_SHAPE_FILL = '#007bff';
    static DEFAULT_SHAPE_STROKE = '#0056b3';
    static DEFAULT_LINE_STROKE = '#000000';
    static DEFAULT_ARROW_FILL = '#000000';
    static ARROW_HEAD_LENGTH = 10;
    static ARROW_HEAD_ANGLE = Math.PI / 6;
    static parseLinePath(linePath) {
        if (!linePath)
            return null;
        const endMatch = linePath.match(/L\s*([-\d.]+)\s*([-\d.]+)/);
        const startMatch = linePath.match(/M\s*([-\d.]+)\s*([-\d.]+)/);
        if (!endMatch || !startMatch)
            return null;
        const startX = parseFloat(startMatch[1]);
        const startY = parseFloat(startMatch[2]);
        const endX = parseFloat(endMatch[1]);
        const endY = parseFloat(endMatch[2]);
        const angle = Math.atan2(endY - startY, endX - startX);
        return { startX, startY, endX, endY, angle };
    }
    static getArrowheadCorners(linePath) {
        const geo = this.parseLinePath(linePath);
        if (!geo)
            return null;
        const { endX, endY, angle } = geo;
        return {
            endX,
            endY,
            head1X: endX - this.ARROW_HEAD_LENGTH * Math.cos(angle - this.ARROW_HEAD_ANGLE),
            head1Y: endY - this.ARROW_HEAD_LENGTH * Math.sin(angle - this.ARROW_HEAD_ANGLE),
            head2X: endX - this.ARROW_HEAD_LENGTH * Math.cos(angle + this.ARROW_HEAD_ANGLE),
            head2Y: endY - this.ARROW_HEAD_LENGTH * Math.sin(angle + this.ARROW_HEAD_ANGLE),
        };
    }
    /**
     * Shortens a line path so it ends at the base of the arrowhead instead of its tip.
     */
    static shortenLineForArrowhead(linePath) {
        const geo = this.parseLinePath(linePath);
        const corners = this.getArrowheadCorners(linePath);
        if (!geo || !corners)
            return linePath;
        const baseX = (corners.head1X + corners.head2X) / 2;
        const baseY = (corners.head1Y + corners.head2Y) / 2;
        return `M ${geo.startX} ${geo.startY} L ${baseX} ${baseY}`;
    }
    /**
     * Returns a closed triangle path for the arrowhead at the end of a line path.
     */
    static createArrowheadPath(linePath) {
        const corners = this.getArrowheadCorners(linePath);
        if (!corners)
            return '';
        const { endX, endY, head1X, head1Y, head2X, head2Y } = corners;
        return `M ${endX} ${endY} L ${head1X} ${head1Y} L ${head2X} ${head2Y} Z`;
    }
    static RFI_INNER_TRANSFORM = 'translate(12,12) scale(0.6) translate(-12,-12)';
    static SUBMITTAL_INNER_TRANSFORM = 'translate(12,12) scale(0.6) translate(-8,-12)';
    static RFI_ICON_PATHS = {
        questionTop: 'M10.2222 9.05551C10.2222 9.05551 10.2506 8.46294 10.8848 7.95265C11.261 7.64959 11.7121 7.56189 12.1184 7.5558C12.4885 7.55106 12.819 7.61235 13.0167 7.70648C13.3553 7.86766 14.0146 8.26113 14.0146 9.09784C14.0146 9.97823 13.439 10.3781 12.7831 10.818C12.1272 11.2578 11.9491 11.7353 11.9491 12.2286',
        questionDot: 'M11.9156 14.6666C12.2896 14.6666 12.5929 14.3634 12.5929 13.9894C12.5929 13.6154 12.2896 13.3122 11.9156 13.3122C11.5416 13.3122 11.2384 13.6154 11.2384 13.9894C11.2384 14.3634 11.5416 14.6666 11.9156 14.6666Z',
        bubble: 'M18.3334 4H5.66671C5.04841 4.00176 4.45594 4.24816 4.01873 4.68536C3.58153 5.12256 3.33513 5.71504 3.33337 6.33333V14.3333C3.33513 14.9516 3.58153 15.5441 4.01873 15.9813C4.45594 16.4185 5.04841 16.6649 5.66671 16.6667H7.33337V20L11.2384 16.7442C11.2983 16.6941 11.374 16.6667 11.4521 16.6667H18.3334C18.9517 16.6649 19.5441 16.4185 19.9813 15.9813C20.4186 15.5441 20.6649 14.9516 20.6667 14.3333V6.33333C20.6649 5.71504 20.4186 5.12256 19.9813 4.68536C19.5441 4.24816 18.9517 4.00176 18.3334 4Z',
    };
    static SUBMITTAL_ICON_PATHS = {
        checkmark: 'M11.25 10.5L7.05 15.5L5.25 13.5',
        document: 'M15.75 8.87109V18C15.75 18.5967 15.5129 19.169 15.091 19.591C14.669 20.0129 14.0967 20.25 13.5 20.25H3C2.40326 20.25 1.83097 20.0129 1.40901 19.591C0.987053 19.169 0.75 18.5967 0.75 18V3C0.75 2.40326 0.987053 1.83097 1.40901 1.40901C1.83097 0.987053 2.40326 0.75 3 0.75H7.62891C8.02659 0.75006 8.40798 0.908044 8.68922 1.18922L15.3108 7.81078C15.592 8.09202 15.7499 8.47341 15.75 8.87109Z',
        fold: 'M8.25 1.125V6.75C8.25 7.14782 8.40804 7.52936 8.68934 7.81066C8.97064 8.09196 9.35218 8.25 9.75 8.25H15.375',
    };
    // ─── Shared methods ───────────────────────────────────────────────────────
    static createId() {
        return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 11)}`;
    }
    static createMarkup(userId, partial) {
        if (!partial.fileId) {
            throw new Error('MarkupDoc requires fileId');
        }
        return {
            _id: partial._id ?? MarkupCommonUtils.createId(),
            created_by: userId,
            created_date: DateUtils.currentIso,
            ...partial,
        };
    }
    /**
     * Returns a new array of elements sorted by their canonical render order.
     * Elements with unlisted types default to order 1.5 (between IMAGE and SUBMITTAL).
     * The original array is not mutated.
     */
    static sortElementsByRenderOrder(elements) {
        return [...elements].sort((a, b) => {
            const orderA = MarkupCommonUtils.RENDER_ORDER[a.type] ?? 1.5;
            const orderB = MarkupCommonUtils.RENDER_ORDER[b.type] ?? 1.5;
            return orderA - orderB;
        });
    }
    /**
     * Create SVG transform string for element rotation.
     */
    static createTransform(element, offsetX = 0, offsetY = 0) {
        if (!element.rotation)
            return '';
        const centerX = offsetX + element.x + element.width / 2;
        const centerY = offsetY + element.y + element.height / 2;
        return `rotate(${element.rotation} ${centerX} ${centerY})`;
    }
    /**
     * Compute lines, fontSize, and lineHeight for TEXT elements.
     */
    static getTextRenderData(element) {
        const lines = (element.content || '').split('\n');
        const fontSize = element.style?.fontSize || 14;
        const lineHeight = fontSize * 1.2;
        return { lines, fontSize, lineHeight };
    }
    /**
     * Compute cx, cy, rx, ry for CIRCLE (ellipse) elements.
     */
    static getEllipseProps(element, offsetX, offsetY) {
        return {
            cx: offsetX + element.x + element.width / 2,
            cy: offsetY + element.y + element.height / 2,
            rx: element.width / 2,
            ry: element.height / 2,
        };
    }
    /**
     * Generate cloud path for cloud markup borders.
     */
    static generateCloudPath(width, height) {
        const bumpSize = Math.min(width, height) * 0.1;
        const bumpCount = 8;
        const path = [];
        const createBumps = (startX, startY, endX, endY, isVertical, bumpOutward) => {
            for (let i = 0; i < bumpCount; i++) {
                const progress = i / bumpCount;
                const nextProgress = (i + 1) / bumpCount;
                if (isVertical) {
                    const y1 = startY + progress * (endY - startY);
                    const y2 = startY + nextProgress * (endY - startY);
                    const midY = (y1 + y2) / 2;
                    const controlX = startX + (bumpOutward ? bumpSize : -bumpSize);
                    path.push(`L ${startX} ${y1}`);
                    path.push(`Q ${controlX} ${midY} ${startX} ${y2}`);
                }
                else {
                    const x1 = startX + progress * (endX - startX);
                    const x2 = startX + nextProgress * (endX - startX);
                    const midX = (x1 + x2) / 2;
                    const controlY = startY + (bumpOutward ? bumpSize : -bumpSize);
                    path.push(`L ${x1} ${startY}`);
                    path.push(`Q ${midX} ${controlY} ${x2} ${startY}`);
                }
            }
        };
        path.push(`M 0 0`);
        createBumps(0, 0, width, 0, false, false);
        createBumps(width, 0, width, height, true, true);
        createBumps(width, height, 0, height, false, true);
        createBumps(0, height, 0, 0, true, false);
        path.push('Z');
        return path.join(' ');
    }
    /**
     * Scale normalized path data (0–1 coordinates) to element dimensions.
     */
    static scaleNormalizedPath(pathData, width, height) {
        if (!pathData)
            return '';
        return pathData.replace(/([ML])\s*([-\d.]+)\s*([-\d.]+)/g, (_match, command, x, y) => {
            const scaledX = parseFloat(x) * width;
            const scaledY = parseFloat(y) * height;
            return `${command} ${scaledX} ${scaledY}`;
        });
    }
    /**
     * Add an arrowhead to a line path.
     */
    static addFixedArrowhead(linePath) {
        const arrowhead = this.createArrowheadPath(linePath);
        if (!arrowhead)
            return linePath;
        return `${this.shortenLineForArrowhead(linePath)} ${arrowhead}`;
    }
    /**
     * Returns the outer transform string that centers and scales a 24×24 pin icon
     * at (centerX, centerY).
     */
    static getPinOuterTransform(centerX, centerY) {
        return `translate(${centerX}, ${centerY}) scale(1.333) translate(-12, -12)`;
    }
}
